import ArrowSvg from '@/svgs/arrow.svg?react';
import * as React from 'react';
import { HTMLAttributes, ReactNode } from 'react';

const Arrow = ({
    variant = 'primary',
    direction,
    className,
    onHovered,
    ...props
}: {
    onHovered?: (hovered: boolean) => void;
    variant?: 'secondary' | 'primary' | 'danger' | 'success';
    direction: 'up' | 'down' | 'left' | 'right';
} & HTMLAttributes<HTMLSpanElement>) => {
    const arrowColor = {
        primary: '#1b7c98',
        secondary: '#a4a4a4',
        danger: '#FF475A',
        success: '#73EFAC',
    };

    const directionClasses = {
        up: '',
        down: 'rotate-180',
        left: '-rotate-90',
        right: 'rotate-90',
    };

    return (
        <span
            onMouseEnter={() => onHovered?.(true)}
            onMouseLeave={() => onHovered?.(false)}
            {...props}
            className={`absolute ${className}`}
        >
            <ArrowSvg
                fill={arrowColor[variant]}
                className={directionClasses[direction]}
            />
        </span>
    );
};

type HoveredState<T> =
    | {
          hoveredValue: T;
          isCurrentValueHovered: false;
          mouseX: number;
      }
    | {
          hoveredValue: T;
          isCurrentValueHovered: true;
          mouseX: number;
      };

type TooltipContentCb<T> = ({
    hoveredValue,
    isCurrentValueHovered,
    mouseX,
}: HoveredState<T>) => ReactNode;

export const BudgetSlider = ({
    value: _value,
    config,
    tooltipContent,
    variable,
}: {
    value: number;
    config: {
        min: number;
        minRecommended: number;
        maxRecommended: number;
        recommendedBudget: number;
        max: number;
        investmentStep?: number;
    };
    tooltipContent: TooltipContentCb<{
        value: number;
        isProfitable: boolean;
        recommendedBudget: number;
        minRecommended: number;
        maxRecommended: number;
        variable: string;
    }>;
    variable: string;
}) => {
    const { min, minRecommended, maxRecommended, recommendedBudget } = config;

    const safeMaxRecommended = maxRecommended > 0 ? maxRecommended : 1;
    const redEnd = (minRecommended / safeMaxRecommended) * 100;
    const greenStart = redEnd;
    const greenWidth = 100 - greenStart;

    const arrowLeft =
        maxRecommended > 0
            ? Math.min(
                  100,
                  Math.max(0, (recommendedBudget / maxRecommended) * 100),
              )
            : 0;

    const [hovered, setHovered] = React.useState<HoveredState<{
        value: number;
        isProfitable: boolean;
        recommendedBudget: number;
        minRecommended: number;
        maxRecommended: number;
        variable: string;
    }> | null>(null);

    const sliderRef = React.useRef<HTMLDivElement>(null);

    const getValueFromPosition = (clientX: number) => {
        if (!sliderRef.current) return min;
        const rect = sliderRef.current.getBoundingClientRect();
        const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
        const ratio = x / rect.width;
        const val = min + ratio * (maxRecommended - min);
        if (config.investmentStep) {
            const step = config.investmentStep;
            return Math.round(val / step) * step;
        }
        return Math.round(val);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const val = getValueFromPosition(e.clientX);
        setHovered({
            hoveredValue: {
                value: val,
                isProfitable: val >= minRecommended && val <= maxRecommended,
                recommendedBudget,
                minRecommended,
                maxRecommended,
                variable,
            },
            isCurrentValueHovered: false,
            mouseX: e.clientX,
        });
    };

    const handleMouseLeave = () => {
        setHovered(null);
    };

    const tooltipStyle: React.CSSProperties = {
        position: 'absolute',
        left: '-100%',
        right: 0,
        bottom: '40px',
        width: '300%',
        pointerEvents: 'none',
        zIndex: 50,
        display: 'flex',
        justifyContent: 'center',
    };

    return (
        <div className="relative w-full h-6" ref={sliderRef}>
            <span
                className="absolute mt-2 h-2 rounded-2xl opacity-80 bg-linear-to-r from-[#FF2D5E8C] from-20% to-[#FF2D5E]"
                style={{
                    left: `0%`,
                    width: `${Math.max(0, redEnd)}%`,
                }}
            />
            <span
                className="absolute mt-2 h-2 rounded-2xl opacity-80 bg-linear-to-r from-[#73EFAC8C] from-20% to-[#73EFAC]"
                style={{
                    left: `${Math.max(0, greenStart)}%`,
                    width: `${Math.max(0, greenWidth)}%`,
                }}
            />
            <Arrow
                variant="success"
                direction="up"
                className="z-20 -translate-x-1/2"
                style={{
                    left: `calc(${arrowLeft}% )`,
                    bottom: 0,
                    position: 'absolute',
                }}
                onHovered={(isHovered) => {
                    if (isHovered && sliderRef.current) {
                        const rect = sliderRef.current.getBoundingClientRect();
                        setHovered({
                            hoveredValue: {
                                value: recommendedBudget,
                                isProfitable:
                                    recommendedBudget >= minRecommended &&
                                    recommendedBudget <= maxRecommended,
                                recommendedBudget,
                                minRecommended,
                                maxRecommended,
                                variable, // pass variable
                            },
                            isCurrentValueHovered: true,
                            mouseX: rect.left + (arrowLeft / 100) * rect.width,
                        });
                    } else {
                        setHovered(null);
                    }
                }}
            />
            <div
                className="absolute inset-0 cursor-pointer"
                style={{ zIndex: 25, background: 'transparent' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            />
            {hovered && (
                <div style={tooltipStyle}>{tooltipContent(hovered)}</div>
            )}
        </div>
    );
};
